import { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../components/button";
import Footer from "../../components/footer";
import Header from "../../components/header";
import Input from "../../components/input";
import { useTranslate } from "../../context/TranslationContext";
import { useGetCategoriesQuery } from "../../redux/Apis/Category";
import {
  useGenerateUploadUrlMutation,
  useGetContentQuery,
} from "../../redux/Apis/General";
import {
  useCreateTemplateMutation,
  useGetTemplateByIdQuery,
  useUpdateTemplateMutation,
} from "../../redux/Apis/Template";

const CreateCustomTemplate = () => {
  const ref = useRef();
  const navigate = useNavigate();
  const { t } = useTranslate("createCustomTemplate");
  const lang = useSelector((state) => state.language.lang);
  const { id: templateId } = useParams();
  const isEdit = Boolean(templateId);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailError, setThumbnailError] = useState("");
  const [formData, setFormData] = useState({
    supportingfiles: null,
  });

  const [selectedCategory, setSelectedCategory] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [submissionMode, setSubmissionMode] = useState();
  const [problemWordCount, setProblemWordCount] = useState(0);

  const { data: categoryData } = useGetCategoriesQuery({ lang });
  useEffect(() => {
    if (!isEdit && categoryData?.data?.length && !selectedCategory) {
      setSelectedCategory(categoryData.data[0]._id);
    }
  }, [categoryData, selectedCategory, isEdit]);

  const [generateUploadUrl] = useGenerateUploadUrlMutation();
  const [createTemplate] = useCreateTemplateMutation();
  const [updateTemplate] = useUpdateTemplateMutation();
  const { data: existingTemplateData, isLoading: isTemplateLoading } =
    useGetTemplateByIdQuery(
      { id: templateId, params: { lang } },
      {
        skip: !isEdit,
      }
    );

  const { data: customTemplatetrack, isLoading: isCustomTemplatetrackLoading } =
    useGetContentQuery({
      search: "custom-templates-track",
      lang,
    });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      title: "",
      audience: "",
      level: "",
      problem: "",
    },
  });

  useEffect(() => {
    if (isEdit && existingTemplateData?.data) {
      const template = existingTemplateData.data;
      reset({
        title: template.title || "",
        audience: template.audience || "",
        level: template.level || "",
        problem: template.problem || "",
      });

      setSelectedCategory(template.category);

      setFormData((prev) => ({
        ...prev,
        supportingfiles: template.supportingfiles,
      }));

      if (template.file) {
        setThumbnailPreview(template.file);
        setFormData((prev) => ({
          ...prev,
          file: template.file,
        }));
      }
    }
  }, [existingTemplateData, isEdit, reset]);

  const handleSupportingFilesChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error(t("pdfAllowed"));
      return;
    }

    const maxSizeMB = 10;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      toast.error(`${t("imageError")} ${maxSizeMB}MB.`);
      return;
    }

    setFormData((prev) => ({ ...prev, supportingfiles: file }));
  };

  const onSubmit = async (data) => {
    if (!thumbnailFile && !isEdit) {
      setThumbnailError("Thumbnail is required.");
      return;
    }
    if (!selectedCategory) {
      toast.info(t("categoryRequired"));
      return;
    }

    setIsSaving(true);
    try {
      let thumbnailKey = formData.file || null;
      if (thumbnailFile instanceof File) {
        const { name, type } = thumbnailFile;
        const { uploadURL, key } = await generateUploadUrl({
          filename: name,
          filetype: type,
          lang: { lang },
        }).unwrap();
        await fetch(uploadURL, {
          method: "PUT",
          headers: { "Content-Type": type },
          body: thumbnailFile,
        });
        thumbnailKey = key;
      }

      let supportingKey = formData.supportingfiles || null;
      if (formData.supportingfiles instanceof File) {
        const { name, type } = formData.supportingfiles;
        const { uploadURL, key } = await generateUploadUrl({
          filename: name,
          filetype: type,
          lang: { lang },
        }).unwrap();
        await fetch(uploadURL, {
          method: "PUT",
          headers: { "Content-Type": type },
          body: formData.supportingfiles,
        });
        supportingKey = key;
      }

      const payload = {
        ...data,
        category: selectedCategory,
        file: thumbnailKey,
        supportingfiles: supportingKey,
        status: "pending",
        type: submissionMode === "track" ? "track" : "template",
      };

      Object.keys(payload).forEach((key) => {
        if (
          payload[key] === "" ||
          payload[key] == null ||
          (Array.isArray(payload[key]) && payload[key].length === 0)
        ) {
          delete payload[key];
        }
      });

      if (isEdit) {
        const res = await updateTemplate({
          id: templateId,
          payload,
          params: { lang },
        }).unwrap();
        toast.success(res.message || t("updateSuccess"));
      } else {
        const res = await createTemplate({
          payload,
          params: { lang },
        }).unwrap();
        toast.success(t("success"));
      }
      navigate("/");
    } catch (err) {
      toast.error(err.message || t("error"));
    } finally {
      setIsSaving(false);
    }
  };

  const templateVideoUrl =
    customTemplatetrack?.data?.sections?.[0]?.content || "";

  const getEmbedUrl = (url, autoplay = true) => {
    if (!url) return null;

    try {
      const urlObj = new URL(url);

      if (
        !urlObj.hostname.includes("youtube.com") &&
        !urlObj.hostname.includes("youtu.be")
      ) {
        return null;
      }

      const videoId = urlObj.searchParams.get("v");
      const listId = urlObj.searchParams.get("list");

      if (listId) {
        return `https://www.youtube.com/embed/videoseries?list=${listId}${
          autoplay ? "&autoplay=1&mute=1" : ""
        }`;
      }

      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}${
          autoplay ? "?autoplay=1&mute=1" : ""
        }`;
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  const embedUrl = getEmbedUrl(templateVideoUrl);

  return (
    <>
      <Header />
      <div
        style={{
          backgroundColor: "#000",
          color: "#fff",
          padding: "40px 0",
          minHeight: "100vh",
          padding: "150px 0 90px 0",
        }}
      >
        {embedUrl && (
          <div className="container mb-5">
            <div className="video-container ratio ratio-16x9 mb-5">
              <iframe
                src={embedUrl}
                title="Custom Template Track Video"
                allow="autoplay; encrypted-media"
                allowFullScreen
                style={{ borderRadius: "12px" }}
              />
            </div>
          </div>
        )}
        <div className="d-flex flex-column justify-content-between align-items-center mb-4 text-center">
          <h2 className="mt-3 text-uppercase">{t("title")}</h2>
          <p className="text-uppercase" style={{ fontSize: "18px" }}>
            {t("subtitle")}
          </p>
        </div>
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{
            padding: "30px",
            maxWidth: "700px",
            margin: "0 auto",
            width: "100%",
            backgroundColor: "#08070c",
            borderRadius: "12px",
            boxShadow: "0 0 10px rgba(0,0,0,0.05)",
            textAlign: "left",
          }}
        >
          <div className="row">
            {isSaving && <Spinner />}

            <div className="col-md-12 mb-3">
              <Controller
                name="type"
                control={control}
                rules={{ required: t("typeRequired") }}
                render={({ field }) => (
                  <Input
                    type="select"
                    label={t("chooseType")}
                    placeholder={t("typePlaceholder")}
                    required
                    options={[
                      {
                        label: t("optionTrack"),
                        value: "track",
                      },
                      {
                        label: t("optionTemplate"),
                        value: "digital_product",
                      },
                    ]}
                    {...field}
                  />
                )}
              />
              <small className="text-muted">{t("typeDescription")}</small>
            </div>

            <div className="col-md-12 mb-3 d-flex flex-column">
              <label className="mainLabel w-100">
                {t("thumbnail")} <span className="text-danger">*</span>
              </label>
              <input
                ref={ref}
                type="file"
                hidden
                accept="image/jpeg,image/png"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (
                    !file ||
                    ![
                      "image/jpeg",
                      "image/png",
                      "image/jpg",
                      "image/webp",
                    ].includes(file.type)
                  ) {
                    setThumbnailError("Only JPG and PNG files are allowed.");
                    return;
                  }
                  setThumbnailError("");
                  setThumbnailFile(file);
                  setThumbnailPreview(URL.createObjectURL(file));
                }}
              />
              {thumbnailError && (
                <div className="text-danger">{thumbnailError}</div>
              )}
              {thumbnailPreview && (
                <img
                  src={thumbnailPreview}
                  alt="thumb"
                  style={{
                    height: "100%",
                    marginTop: 10,
                    borderRadius: 12,
                    width: "100%",
                    objectFit: "cover",
                  }}
                />
              )}
              <div className="col-12 d-flex">
                <Button
                  className="mt-3"
                  text={t("chooseImage")}
                  variant="primaryButton"
                  onClick={(e) => {
                    e.preventDefault();
                    ref.current.click();
                  }}
                />
              </div>
            </div>

            <div className="col-md-12 mb-3">
              <Controller
                name="title"
                control={control}
                rules={{ required: t("titleRequired") }}
                render={({ field }) => (
                  <Input
                    label={t("titleLabel")}
                    required
                    placeholder={t("titlePlaceholder")}
                    {...field}
                  />
                )}
              />
              {errors.title && (
                <div className="text-danger mt-1">{errors.title.message}</div>
              )}
            </div>

            <div className="col-md-12 mb-3">
              <Input
                type="select"
                label={t("trackAbout")}
                placeholder={t("categoryPlaceholder")}
                value={selectedCategory}
                options={
                  categoryData?.data?.map((c) => ({
                    label: c.name,
                    value: c._id,
                  })) || []
                }
                required
                onChange={(e) => setSelectedCategory(e.target.value)}
              />
            </div>

            <div className="col-md-12 mb-3">
              <Controller
                name="audience"
                control={control}
                rules={{ required: t("audienceRequired") }}
                render={({ field }) => (
                  <Input
                    label={t("targetAudience")}
                    required
                    placeholder={t("audiencePlaceholder")}
                    {...field}
                  />
                )}
              />
              {errors.audience && (
                <div className="text-danger mt-1">
                  {errors.audience.message}
                </div>
              )}
            </div>

            <div className="col-md-12 mb-3">
              <Controller
                name="level"
                control={control}
                rules={{ required: t("levelRequired") }}
                render={({ field }) => (
                  <Input
                    type="select"
                    label={t("level")}
                    placeholder={t("levelPlaceholder")}
                    required
                    options={[
                      { label: "Beginner", value: "beginner" },
                      { label: "Intermediate", value: "intermediate" },
                      { label: "Advanced", value: "advanced" },
                    ]}
                    {...field}
                  />
                )}
              />
              {errors.level && (
                <div className="text-danger mt-1">{errors.level.message}</div>
              )}
            </div>

            <div className="col-md-12 mb-3">
              <Controller
                name="problem"
                control={control}
                rules={{
                  required: t("problemRequired"),
                  validate: (value) => {
                    const wordCount = value
                      .trim()
                      .split(/\s+/)
                      .filter(Boolean).length;
                    return wordCount <= 100 || "Maximum 100 words allowed";
                  },
                }}
                render={({ field }) => {
                  const handleChange = (e) => {
                    const input = e.target.value;
                    const words = input.trim().split(/\s+/).filter(Boolean);
                    const wordCount = words.length;

                    if (wordCount <= 100) {
                      setProblemWordCount(wordCount);
                      field.onChange(input);
                    }
                  };

                  return (
                    <>
                      <Input
                        type="textarea"
                        rows={3}
                        label={t("problem")}
                        required
                        placeholder={t("problemPlaceholder")}
                        value={field.value}
                        onChange={handleChange}
                      />
                      <div
                        className={`text-end small ${
                          problemWordCount > 100 ? "text-danger" : "text-muted"
                        }`}
                      >
                        {problemWordCount} / 100 {t("wordCount")}
                      </div>
                    </>
                  );
                }}
              />
              {errors.problem && (
                <div className="text-danger mt-1">{errors.problem.message}</div>
              )}
            </div>

            <div className="col-md-12 mb-3">
              <label className="mainLabel mb-2">{t("uploadOptional")}</label>
              <input
                type="file"
                className="site-input"
                accept="application/pdf"
                onChange={handleSupportingFilesChange}
              />
              {typeof formData.supportingfiles === "string" && (
                <div className="mt-2">
                  <a
                    href={`https://asset.traineego.com/${formData.supportingfiles}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("viewFile")}
                  </a>
                </div>
              )}
            </div>

            <div className="col-12 d-flex justify-content-center">
              <Button
                className="d-flex align-items-center justify-content-center mt-3"
                text={
                  isSaving ? (
                    <Spinner className="text-white" />
                  ) : submissionMode === "digital_product" ? (
                    t("requestTemplate")
                  ) : (
                    t("requestTrack")
                  )
                }
                variant="primaryButton"
                disabled={isSaving}
              />
            </div>
          </div>
        </form>
      </div>
      <Footer />
    </>
  );
};

export default CreateCustomTemplate;
